import React, { useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	InputAdornment,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	Alert,
	Paper,
	Chip,
	Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';

import type { IProviderPaymentCalendarCellDetailRequestDTO, IProviderPaymentCalendarDetailItemDTO } from '../../types';
import { getProviderPaymentCalendarCellDetail } from '../../services';

import { handleOpenSaleOrderDetail, serviceTypeToEmoji } from '../../../salesOrder/constants';
import DealOrCaseOpenDetail from '../../../common/components/DealOrCaseOpenDetail';
import { formatDateToShowUser } from '../../../common/helpers';

// Helper de moneda
const money = (n: number): string => n.toFixed(2).replace('.', ',');

interface Props {
	open: boolean;
	onClose: () => void;
	requestParams: IProviderPaymentCalendarCellDetailRequestDTO | null;
}

export const ProviderPaymentCalendarDetailModal: React.FC<Props> = ({ open, onClose, requestParams }: Props) => {
	const [loading, setLoading] = useState(false);
	const [items, setItems] = useState<IProviderPaymentCalendarDetailItemDTO[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [error, setError] = useState<string | null>(null);

	// Cargar datos al abrir
	useEffect(() => {
		if (!open || !requestParams) return;

		let mounted = true;
		setLoading(true);
		setError(null);
		setItems([]);
		setSearchTerm('');

		getProviderPaymentCalendarCellDetail(requestParams)
			.then((resp) => {
				if (mounted) {
					setItems(resp.data || []);
				}
			})
			.catch(() => {
				if (mounted) {
					setError('Ocurrió un error al cargar el detalle del pago.');
				}
			})
			.finally(() => {
				if (mounted) {
					setLoading(false);
				}
			});

		return () => {
			mounted = false;
		};
	}, [open, requestParams]);

	// Filtro interno
	const filteredItems = useMemo(() => {
		if (!searchTerm) return items;
		const lower = searchTerm.toLowerCase();
		return items.filter((it) => {
			return (
				(it.serviceId || '').toLowerCase().includes(lower) ||
				(it.providerFantasyName || '').toLowerCase().includes(lower) ||
				(it.providerBusinessName || '').toLowerCase().includes(lower) ||
				(it.saleOrderId || '').toLowerCase().includes(lower) ||
				(it.serviceType || '').toLowerCase().includes(lower) ||
				(it.serviceReference || '').toLowerCase().includes(lower) ||
				(it.dealId?.toString() || '').includes(lower)
			);
		});
	}, [items, searchTerm]);

	// Calcular total visible
	const totalVisible = useMemo(() => {
		return filteredItems.reduce((acc, curr) => acc + curr.amount, 0);
	}, [filteredItems]);

	// Helper para renderizar el Tipo de Servicio bonito
	const renderServiceType = (type: string) => {
		const info = serviceTypeToEmoji[type];
		if (!info) {
			return <Typography variant="body2">{type}</Typography>;
		}
		return (
			<Chip
				label={`${info.emoji} ${info.name}`}
				size="small"
				variant="outlined"
				sx={{ borderColor: '#e0e0e0', bgcolor: '#fafafa' }}
			/>
		);
	};

	return (
		<Dialog
			open={open}
			// Bloquear cierre al hacer clic afuera (backdrop) o escape
			onClose={(_, reason) => {
				if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
					onClose();
				}
			}}
			maxWidth="lg" // Más ancho para acomodar las columnas
			fullWidth
		>
			<DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Box>
					<Typography variant="h6" component="div" sx={{ color: '#4b2c71', fontWeight: 700 }}>
						Detalle de Servicios a Pagar
					</Typography>
					{requestParams && (
						<Typography variant="caption" color="text.secondary">
							Fecha de pago: <strong>{formatDateToShowUser(requestParams.paymentDate)}</strong>
						</Typography>
					)}
				</Box>
				<IconButton
					aria-label="close"
					onClick={onClose}
					sx={{
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ p: 2 }}>
				{error && (
					<Box mb={2}>
						<Alert severity="error">{error}</Alert>
					</Box>
				)}

				{/* Buscador Interno */}
				<Box mb={2}>
					<TextField
						fullWidth
						size="small"
						placeholder="Buscar por ID Servicio, Deal, Proveedor, Referencia..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon color="action" />
								</InputAdornment>
							),
						}}
					/>
				</Box>

				{loading ? (
					<Box display="flex" justifyContent="center" py={8}>
						<CircularProgress size={40} sx={{ color: '#4b2c71' }} />
					</Box>
				) : (
					<TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
						<Table stickyHeader size="small" sx={{ minWidth: 900 }}>
							{' '}
							{/* minWidth para scroll lateral */}
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 'bold', width: 100 }}>ID Servicio</TableCell>
									<TableCell sx={{ fontWeight: 'bold', width: 120 }}>Deal / Caso</TableCell>
									<TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Proveedor</TableCell>
									<TableCell sx={{ fontWeight: 'bold', width: 150 }}>Tipo</TableCell>
									<TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Referencia</TableCell>
									<TableCell align="right" sx={{ fontWeight: 'bold', width: 120 }}>
										Monto (USD)
									</TableCell>
									<TableCell align="center" sx={{ fontWeight: 'bold', width: 80 }}>
										Acción
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filteredItems.length > 0 ? (
									filteredItems.map((row, idx) => (
										<TableRow key={`${row.saleOrderId}-${row.serviceId}-${idx}`} hover>
											{/* ID Servicio */}
											<TableCell>
												<Typography variant="body2" fontWeight={600} color="text.primary">
													{row.serviceId}
												</Typography>
											</TableCell>

											{/* Deal / Caso (Usando tu componente) */}
											<TableCell>
												<Box display="flex" alignItems="center">
													<DealOrCaseOpenDetail
														dealId={row.dealId}
														isBitrixDeal={row.isBitrixDeal}
														// Si quieres que parezca botón o link, ajusta props aquí:
														// renderAsButton={false}
													/>
												</Box>
											</TableCell>

											{/* Proveedor */}
											<TableCell>
												<Typography variant="body2" fontWeight={500}>
													{row.providerFantasyName}
												</Typography>
												{row.providerBusinessName && row.providerBusinessName !== row.providerFantasyName && (
													<Typography variant="caption" color="text.secondary" display="block">
														{row.providerBusinessName}
													</Typography>
												)}
											</TableCell>

											{/* Tipo Servicio (Emoji) */}
											<TableCell>{renderServiceType(row.serviceType)}</TableCell>

											{/* Referencia */}
											<TableCell>
												<Tooltip title={row.serviceReference || ''}>
													<Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
														{row.serviceReference || '-'}
													</Typography>
												</Tooltip>
											</TableCell>

											{/* Monto */}
											<TableCell align="right">
												<Typography variant="body2" fontWeight={700} color="#4b2c71">
													{money(row.amount)}
												</Typography>
											</TableCell>

											{/* Botón Ver Servicio */}
											<TableCell align="center">
												<Tooltip title="Ver detalle del servicio">
													<IconButton
														size="small"
														color="primary"
														onClick={handleOpenSaleOrderDetail(row.saleOrderId, row.serviceId)}
													>
														<VisibilityIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={7} align="center" sx={{ py: 4 }}>
											<Typography variant="body2" color="text.secondary">
												No se encontraron resultados para `{searchTerm}`
											</Typography>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				)}

				{/* Footer con total */}
				{!loading && filteredItems.length > 0 && (
					<Box
						mt={2}
						p={2}
						bgcolor="#fafafa"
						borderRadius={1}
						display="flex"
						justifyContent="flex-end"
						alignItems="center"
						gap={2}
						border={'1px solid #eee'}
					>
						<Typography variant="body2" color="text.secondary">
							Total visible ({filteredItems.length} items):
						</Typography>
						<Typography variant="h5" color="#4b2c71" fontWeight={700}>
							USD {money(totalVisible)}
						</Typography>
					</Box>
				)}
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={onClose} variant="outlined" color="inherit">
					Cerrar
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ProviderPaymentCalendarDetailModal;
